import { useName } from '@bechtel/client-common';
import { AvatarSize, Box, Button, ButtonAppearance, Icon, IconSize, Text, TextFieldSize } from '@bechtel/gds';
import { ProjectSupportUser } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Field, FormikProps } from 'formik';
import { filter, map } from 'lodash';
import React, { useState } from 'react';
import { FormikState } from '~/general';
import { getPriorityRoleKey } from '~/user-admin/users/utils/role-display';
import { FieldError } from './common';
import { AvatarRegularStyled, TextInputStyled, UserItemStyled } from './styled';

interface UserItemProps {
  item: ProjectSupportUser;
  index: number;
  form: FormikProps<FormikState>;
}

export const UserItem: React.FC<UserItemProps> = ({ item, index, form }) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  const [isEditable, setIsEditable] = useState(false);
  const [orgItem, setOrgItem] = useState<ProjectSupportUser>(item);
  const { email, phone, location } = item;
  const { firstName, lastName, roles } = item.user;
  const { imageUrl } = item.user.crewMember;
  const roleDisplayText = getPriorityRoleKey(roles || []);
  const { dirty, isValid, errors } = form;
  const resetItem = () => {
    const { email, phone, location } = orgItem;
    form.setFieldValue(`projectSupportUsers[${index}].email`, email);
    form.setFieldValue(`projectSupportUsers[${index}].phone`, phone);
    form.setFieldValue(`projectSupportUsers[${index}].location`, location);
    setIsEditable(false);
    errors.projectSupportUsers = [];
  };
  return (
    <Box
      flexDirection={{ sm: 'column' }}
      css={`
        ${({ theme }) => `border-bottom: 1px solid ${theme.colors.grey[300]};`}
      `}
      data-testid={`usercard-${index}`}
    >
      <Box
        flexDirection={{ sm: 'row' }}
        css={`
          max-width: 430px;
        `}
      >
        <Box alignItems={{ sm: 'flex-start' }} flexBasis={{ sm: '50%' }}>
          <UserItemStyled>
            <Box mr={{ sm: '500' }}>
              <AvatarRegularStyled
                size={AvatarSize.LARGE}
                hasImageUrl={imageUrl ? true : false}
                src={imageUrl ? imageUrl : undefined}
              />
            </Box>
            <Box flexDirection={{ sm: 'column' }}>
              <Box pt={{ sm: '100' }} pb={{ sm: '200' }} style={{ whiteSpace: 'nowrap' }}>
                <Text size="300" color="grey.1000" weight="600">
                  {getFullName(firstName, lastName)}
                </Text>
              </Box>
              <Box pb={{ sm: '200' }}>
                <Text size="200" color="grey.600">
                  {roleDisplayText}
                </Text>
              </Box>
            </Box>
          </UserItemStyled>
        </Box>
        {!isEditable && (
          <Box alignItems={{ sm: 'flex-start' }} flexDirection={{ sm: 'column' }} flexBasis={{ sm: '45%' }}>
            <Box py={{ sm: '450' }} style={{ whiteSpace: 'nowrap' }}>
              <Text size="300" color="grey.1000">
                {email}
              </Text>
            </Box>
            <Box py={{ sm: '450' }}>
              <Text size="300" color="grey.1000">
                {phone}
              </Text>
            </Box>
            <Box py={{ sm: '450' }}>
              <Text size="300" color="grey.1000">
                {location}
              </Text>
            </Box>
          </Box>
        )}
        {isEditable && (
          <Box alignItems={{ sm: 'flex-start' }} flexDirection={{ sm: 'column' }} flexBasis={{ sm: '45%' }}>
            <Box py={{ sm: '200' }} style={{ whiteSpace: 'nowrap' }} flexDirection={{ sm: 'column' }}>
              <Field name={`projectSupportUsers[${index}].email`}>
                {({ field }) => {
                  return <TextInputStyled size={TextFieldSize.SMALL} {...field} placeholder={t`Email`} />;
                }}
              </Field>
              {!!errors.projectSupportUsers &&
                filter(errors.projectSupportUsers, (error) => error?.email).length !== 0 && (
                  <FieldError msg={i18n._(map(errors.projectSupportUsers, (error) => error?.email).join(''))} />
                )}
            </Box>
            <Box py={{ sm: '200' }} flexDirection={{ sm: 'column' }}>
              <Field name={`projectSupportUsers[${index}].phone`}>
                {({ field }) => {
                  return (
                    <TextInputStyled size={TextFieldSize.SMALL} {...field} placeholder={t`Phone`} maxLength="12" />
                  );
                }}
              </Field>
              {!!errors.projectSupportUsers &&
                filter(errors.projectSupportUsers, (error) => error?.phone).length !== 0 && (
                  <FieldError msg={i18n._(map(errors.projectSupportUsers, (error) => error?.phone).join(''))} />
                )}
            </Box>
            <Box py={{ sm: '200' }} flexDirection={{ sm: 'column' }}>
              <Field name={`projectSupportUsers[${index}].location`}>
                {({ field }) => {
                  return <TextInputStyled size={TextFieldSize.SMALL} {...field} placeholder={t`Location`} />;
                }}
              </Field>
              {!!errors.projectSupportUsers &&
                filter(errors.projectSupportUsers, (error) => error?.location).length !== 0 && (
                  <FieldError msg={i18n._(map(errors.projectSupportUsers, (error) => error?.location).join(''))} />
                )}
            </Box>
            <Box mr={{ sm: '550' }} alignSelf={{ sm: 'flex-end' }} style={{ marginTop: 'auto' }}>
              <Box mr={{ sm: '400' }}>
                <Button appearance={ButtonAppearance.TERTIARY} onClick={() => resetItem()}>
                  <Text color="blue.800" size="400" weight="600">
                    <Trans>Cancel</Trans>
                  </Text>
                </Button>
              </Box>
              <Button
                appearance={ButtonAppearance.TERTIARY}
                onClick={() => setIsEditable(false)}
                disabled={!dirty || !isValid}
              >
                <Text color="blue.800" size="400" weight="600">
                  <Trans>Done</Trans>
                </Text>
              </Button>
            </Box>
          </Box>
        )}
        {!isEditable && (
          <Box alignItems={{ sm: 'flex-start' }} pt={{ sm: '450' }} flexBasis={{ sm: '5%' }}>
            <Icon
              size={IconSize.LARGE}
              onClick={() => {
                setOrgItem(item);
                setIsEditable(true);
              }}
              icon="edit"
              fillColor="blue.800"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserItem;
